import { QueryCopilotData } from '@/pages/api/map-events/query-copilot.types';
import { Formatter } from '../utils/Formatter';

export type ParsedChunk = {
  event: 'answer_token' | 'citations_token';
  token: string;
  token_index: number;
  timestamp: string;
};

export type LinkedDocument = {
  citation_number: string;
  raw_content: string;
  metadata: {
    file_path: string;
    doc_name: string;
    start_char: number;
    end_char: number;
  };
};

export type ParsedLinkedDocuments = {
  linked_documents: LinkedDocument[];
};

export type ExtractedDate = {
  start: string;
  end: string;
  label?: string;
};

export type ParsedDateExtraction = {
  event: 'date_extraction';
  extracted_dates: ExtractedDate[];
  awaiting_confirmation: boolean;
  timestamp: string;
};

export type ParsedLine = ParsedChunk | ParsedLinkedDocuments | ParsedDateExtraction | { error?: string };

// Detects token events in JSON-per-line stream chunks.
export const isParsedChunk = (data: ParsedLine): data is ParsedChunk =>
  'event' in data && (data.event === 'answer_token' || data.event === 'citations_token');

// Detects linked document payloads emitted alongside answer tokens.
export const isParsedLinkedDocuments = (data: ParsedLine): data is ParsedLinkedDocuments => 'linked_documents' in data;

// Detects date extraction events that require user confirmation.
export const isParsedDateExtraction = (data: ParsedLine): data is ParsedDateExtraction =>
  'event' in data && data.event === 'date_extraction';

// Normalizes selected date ranges to YYYY-MM-DD when needed.
export const formatSelectedDates = (
  selected_dates: ExtractedDate[] | undefined,
): QueryCopilotData['selected_dates'] => {
  if (!selected_dates) return undefined;

  return selected_dates.map((date) => ({
    start: Formatter.normalizeDate(date.start, { format: Formatter.DATE_FORMAT.YYYY_MM_DD }),
    end: Formatter.normalizeDate(date.end, { format: Formatter.DATE_FORMAT.YYYY_MM_DD }),
    label: date.label,
  }));
};
